import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';
import { UserProvider } from 'src/utils/UserContext';
import ApplicationRoutes from 'src/core/routing/ApplicationRoutes';
import AppInitWrapper from './components/AppInitWrapper';
import AuthenticationWrapper from './utils/AuthenticationWrapper';
import RegionRedirectBanner from './components/RegionRedirectBanner';
import { consumeRedirectMarker, runRegionRedirect } from './utils/RegionRedirect';


import '@amzn/katal-components/styles.css';
import './index.scss';
import './core/styles/coreStyles.css';
// Create QueryClient outside of component to ensure it's not recreated on renders
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 300000, // 5 minutes
            cacheTime: 3600000, // 1 hour
            suspense: false,
        },
    },
});

function App({ redirectedFromDub }: { redirectedFromDub: boolean }) {
    return (
        <QueryClientProvider client={queryClient}>
            <RegionRedirectBanner show={redirectedFromDub} />
            <UserProvider>
                <BrowserRouter>
                    <AuthenticationWrapper>
                        <AppInitWrapper>
                            <ApplicationRoutes />
                        </AppInitWrapper>
                    </AuthenticationWrapper>
                </BrowserRouter>
            </UserProvider>
        </QueryClientProvider>
    );
}

// Blanket DUB(EU) -> ZAZ Midway-door redirect: bounce the whole EU-door session
// to the ZAZ host before React mounts so no DUB Midway auth or backend call is
// made. No-ops on AFT-X hosts, outside prod, and off the EU door (NA/NRT/ZAZ).
if (!runRegionRedirect()) {
    // On the ZAZ side of that redirect, consume (read + strip) the URL marker
    // appended by the redirect BEFORE React mounts, and surface a one-time
    // banner telling the operator why the URL changed.
    const redirectedFromDub = consumeRedirectMarker();

    const container = document.getElementById('root');
    if (!container) throw new Error('Failed to find the root element');

    const root = createRoot(container);
    root.render(
        <React.StrictMode>
            <App redirectedFromDub={redirectedFromDub} />
        </React.StrictMode>
    );
}
